/**
 * Account-wide starting values that pre-fill creation forms for everyone on the account
 * (ENG-2747). Mirrors `AccountSetting::ACCOUNT_DEFAULT_CODES` in gpx-engine.
 */
export const ACCOUNT_DEFAULT_CODE = {
  stopRadiusOrigin: 'default_stop_radius_origin',
  stopRadiusDestination: 'default_stop_radius_destination',
  stopRadiusWaypoint: 'default_stop_radius_waypoint',
  routeCorridorBuffer: 'default_route_corridor_buffer',
  geofenceAlertOnEnterExit: 'default_geofence_alert_on_enter_exit',
  geofenceMaxDwellDays: 'default_geofence_max_dwell_days',
  geofenceMaxAssets: 'default_geofence_max_assets',
  geofenceMinAssets: 'default_geofence_min_assets',
} as const;

export type AccountDefaultCode =
  (typeof ACCOUNT_DEFAULT_CODE)[keyof typeof ACCOUNT_DEFAULT_CODE];

export type AccountSettingsMap = Record<string, string | number | null>;

/**
 * Values arrive as text and a missing row is the normal state — the account has never
 * set this default, which is not the same as setting it to zero.
 */
export function readAccountDefaultNumber(
  settings: AccountSettingsMap | undefined,
  code: AccountDefaultCode,
): number | null {
  const value = settings?.[code];
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function readAccountDefaultBoolean(
  settings: AccountSettingsMap | undefined,
  code: AccountDefaultCode,
): boolean | null {
  const value = settings?.[code];
  if (value === undefined || value === null || value === '') return null;
  return !!+value;
}

/**
 * The geofence defaults in the shape `GeofenceForm` takes as initial values. A null
 * allowable parameter is the built-in "no limit", which is why only the toggle
 * collapses to a concrete value.
 */
export function geofenceCreationDefaults(
  settings: AccountSettingsMap | undefined,
) {
  return {
    is_alertable:
      readAccountDefaultBoolean(
        settings,
        ACCOUNT_DEFAULT_CODE.geofenceAlertOnEnterExit,
      ) ?? false,
    allowable_dwell_days: readAccountDefaultNumber(
      settings,
      ACCOUNT_DEFAULT_CODE.geofenceMaxDwellDays,
    ),
    max_assets: readAccountDefaultNumber(
      settings,
      ACCOUNT_DEFAULT_CODE.geofenceMaxAssets,
    ),
    min_assets: readAccountDefaultNumber(
      settings,
      ACCOUNT_DEFAULT_CODE.geofenceMinAssets,
    ),
  };
}

/** The same defaults as `geofence/store` params, for the paths that skip the form. */
export function geofenceCreationDefaultParams(
  settings: AccountSettingsMap | undefined,
) {
  const { is_alertable, ...allowable } = geofenceCreationDefaults(settings);
  return { ...allowable, is_alertable: is_alertable ? 1 : 0 };
}
